/** A step's `content` section as the Sitecore adapter still keeps it, whole, beside the fields it lifted out. */
interface KeptContentSection {
  title?: string;
  content?: string;
}

/** The part of a step's content this reads: see `readContentSection`. */
export interface ContentSectionFields {
  /** The heading. `title` is also the dialog section's, so both CMSs name this copy after its section. */
  Content_title?: string;
  /**
   * The body, as HTML, on Sanity. On Sitecore this name still holds the kept `content` section,
   * the body inside it: read it through `readContentSection` rather than directly.
   */
  content?: string | KeptContentSection;
}

/** Sitecore's kept section, as opposed to the body Sanity keeps under the same name. */
const isSection = (value: ContentSectionFields['content']): value is KeptContentSection => typeof value === 'object';

/**
 * A step's heading and body, from whichever CMS answered.
 *
 * The generic choice steps keep both in a Sitecore section called `content`, as its fields `title`
 * and `content`, and those are the two names that do not come through flat:
 * - `title` is also the `moreInfoDialog` section's, so both CMSs call this copy `Content_title`. The
 *   Sitecore adapter offers that name from its collision table; where it does not, the kept section
 *   still holds `title`.
 * - `content`, the body, is the top-level `content` on Sanity. On Sitecore that name is the section's
 *   own, and the adapter never lifts a member onto a name the rendering already uses, section names
 *   included, whether or not it keeps the sections (`liftFieldGroups` in `libs/cms-sitecore`). So the
 *   body stays inside the section.
 *
 * Delete this once the Sitecore adapter offers a section's same-named member under that name: then the
 * body reads as `content` on both lanes, and `Content_title` is all the heading needs. Turning off
 * `KEEP_SITECORE_FIELD_GROUPS` alone is not enough, because the section's name stays reserved either
 * way, so the body would be dropped with the section rather than lifted.
 */
export const readContentSection = (fields: ContentSectionFields | undefined): { title?: string; body?: string } => {
  const value = fields?.content;
  if (isSection(value)) return { title: fields?.Content_title ?? value.title, body: value.content };
  return { title: fields?.Content_title, body: value };
};
